import React from 'react';
import type {
    CatalogImportListItem,
    CatalogImportListPage,
} from '@/api/models';
import { useCatalogImportsQuery } from '@/api/queries/catalogImports';
import {
    isBranchScopeRefusal,
    useCatalogImport,
} from '@/pages/catalogImport/store';
import { useIsNarrowViewport } from '@/utils';

/** A read that has a result on screen: settled, running again, or failed again. */
export type ImportHistoryRefresh = 'idle' | 'pending' | 'error';

/** Where the shown page sits in the history, and the way to the others. */
export type ImportHistoryPages = {
    /** Positions of the shown page's first and last import, from 1. */
    from: number;
    to: number;
    totalCount: number;
    canGoBack: boolean;
    canGoOn: boolean;
    onPrevious: () => void;
    onNext: () => void;
};

/** The history section's states (rulebook §12). */
export type ImportHistoryState =
    | { status: 'loading' }
    | { status: 'error'; onRetry: () => void }
    | {
          status: 'empty';
          refresh: ImportHistoryRefresh;
          onRetry: () => void;
      }
    | {
          status: 'ready';
          /** The page's imports, newest first, undone ones included. */
          items: CatalogImportListItem[];
          /** `null` when the history fits on one page. */
          pagination: ImportHistoryPages | null;
          refresh: ImportHistoryRefresh;
          onRetry: () => void;
      };

type HistoryStateInput = {
    /** The page on screen; while another page loads, still the last one. */
    data: CatalogImportListPage | undefined;
    isError: boolean;
    isFetching: boolean;
    /** The page asked for, from 1. */
    page: number;
    onRetry: () => void;
    onPrevious: () => void;
    onNext: () => void;
};

/**
 * What the history shows. Before any page arrives it is loading, or an error
 * once the read failed and stopped; after that the page stays on screen
 * while the next read runs (`pending`) or after it failed (`error`), so a
 * refresh never empties the table. An empty history is said once, with no
 * pages.
 *
 * Previous and Next follow the page asked for, so a second press while a page
 * loads goes one further; the summary describes the rows on screen.
 */
export const importHistoryStateOf = ({
    data,
    isError,
    isFetching,
    page,
    onRetry,
    onPrevious,
    onNext,
}: HistoryStateInput): ImportHistoryState => {
    if (data === undefined) {
        return isError && !isFetching
            ? { status: 'error', onRetry }
            : { status: 'loading' };
    }
    const refresh: ImportHistoryRefresh = isFetching
        ? 'pending'
        : isError
          ? 'error'
          : 'idle';
    if (data.totalCount === 0) {
        return { status: 'empty', refresh, onRetry };
    }
    const from = (data.page - 1) * data.pageSize + 1;
    return {
        status: 'ready',
        items: data.items,
        pagination:
            data.totalPages > 1
                ? {
                      from,
                      to: from + data.items.length - 1,
                      totalCount: data.totalCount,
                      canGoBack: page > 1,
                      canGoOn: page < data.totalPages,
                      onPrevious,
                      onNext,
                  }
                : null,
        refresh,
        onRetry,
    };
};

export type UseImportHistoryResult = {
    history: ImportHistoryState;
    /** A phone (below `sm`): the table keeps the file and the value only. */
    isNarrow: boolean;
};

/**
 * One branch's import history under the in-shell page's Upload: the
 * branch's imports, newest first, a page at a time as the API pages them
 * (`useCatalogImportsQuery`, 20 a page).
 *
 * - **Fresh after an import.** The import invalidates the branch's history,
 *   and the section mounts again with File (Import another file), so the
 *   new import is listed without a reload; the earlier page stays on screen
 *   while the read runs.
 * - **Pages** are local: the history starts at the first, newest page each
 *   time File opens.
 * - **Refusal.** When the server refuses the branch to this user
 *   (`BRANCH_NOT_ALLOWED`), the store hears of it (`branchRefused`) and the
 *   page shows its forbidden notice in place of the step and the history.
 */
export const useImportHistory = (branchId: string): UseImportHistoryResult => {
    const [page, setPage] = React.useState(1);
    const query = useCatalogImportsQuery(branchId, page);
    const { actions } = useCatalogImport();
    const isNarrow = useIsNarrowViewport();

    const isRefused = isBranchScopeRefusal(query.error);
    React.useEffect(() => {
        if (isRefused) {
            actions.branchRefused();
        }
    }, [actions, isRefused]);

    const { refetch } = query;
    const onRetry = React.useCallback(() => {
        void refetch();
    }, [refetch]);
    const onPrevious = React.useCallback(
        () => setPage((current) => Math.max(1, current - 1)),
        [],
    );
    const onNext = React.useCallback(
        () => setPage((current) => current + 1),
        [],
    );

    const history = React.useMemo(
        () =>
            importHistoryStateOf({
                data: query.data,
                isError: query.isError,
                isFetching: query.isFetching,
                page,
                onRetry,
                onPrevious,
                onNext,
            }),
        [
            query.data,
            query.isError,
            query.isFetching,
            page,
            onRetry,
            onPrevious,
            onNext,
        ],
    );

    return { history, isNarrow };
};
